import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Button,
  Chip,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  TablePagination,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useMutation, useQuery } from "@apollo/client/react";
import { GET_CLASS_LIST, GET_MY_PURCHASED_PASSES } from "../graphql/query";
import { BOOK_CLASS_WITH_PASS } from "../graphql/mutation";
import { formatTo12Hour } from "../utils/time";
import { CLASS_TYPE_UI } from "../utils/const";
import CenteredLoader from "./CenteredLoader";
import CustomPaginationActions from "./CustomPaginationActions";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CustomSnackbar from "./Snackbar";
import ClassPurchaseDrawer from "./ClassPurchaseDrawer";
import type { GetMyPurchasedPassesResponse } from "../interfaces/studentPassPurchase.interface";
import TruncatedTitle from "./TruncatedTitle";

interface UserCalenderClassesCardViewProps {
  startDate: string;
  endDate: string;
  selectedDayForFilter: string | null;
  onView: (id: string) => void;
}

const getJoinedClassState = (purchases: GetMyPurchasedPassesResponse["getMyPurchasedPasses"]["data"] | undefined) => {
  const sessionKeys = new Set<string>();

  purchases?.forEach((purchase) => {
    if (purchase?.status !== "ACTIVE") return;
    if (Array.isArray(purchase.bookedSessions) && purchase.bookedSessions.length > 0) {
      purchase.bookedSessions.forEach((session) => {
        if (session?.classId && session?.sessionDate) {
          sessionKeys.add(`${session.classId}|${session.sessionDate}`);
        }
      });
      return;
    }
    const bookedDates = Array.isArray(purchase.bookedSessionDates)
      ? purchase.bookedSessionDates
      : purchase.bookedSessionDate
        ? [purchase.bookedSessionDate]
        : [];
    if (purchase.classId && bookedDates.length > 0) {
      bookedDates.forEach((date) => sessionKeys.add(`${purchase.classId}|${date}`));
    }
  });

  return { sessionKeys };
};

const UserCalenderClassesCardView: React.FC<UserCalenderClassesCardViewProps> = ({
  startDate,
  endDate,
  selectedDayForFilter,
  onView: _onView,
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedActivity, setSelectedActivity] = useState<string | null>(null);
  const [selectedInstructor, setSelectedInstructor] = useState<string | null>(null);
  const [activityAnchorEl, setActivityAnchorEl] = useState<null | HTMLElement>(null);
  const [instructorAnchorEl, setInstructorAnchorEl] = useState<null | HTMLElement>(null);

  const [page, setPage] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 3 : 9);

  // Modal state for booking
  const [selectedClassToBook, setSelectedClassToBook] = useState<any | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error" | "info">("info");
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setRowsPerPage(isMobile ? 3 : 9);
    setPage(0);
  }, [isMobile]);

  const { data, loading, refetch } = useQuery(GET_CLASS_LIST, {
    variables: {
      page: 1,
      perPage: 300,
      search: searchQuery || "",
      startDate,
      endDate,
      includeCancelled: false,
    },
    fetchPolicy: "network-only",
  });

  const { data: myPassesData, refetch: refetchMyPasses } = useQuery<GetMyPurchasedPassesResponse>(
    GET_MY_PURCHASED_PASSES,
    { fetchPolicy: "network-only" }
  );
  const [bookClassWithPass] = useMutation(BOOK_CLASS_WITH_PASS);

  const joinedClassState = useMemo(() => {
    return getJoinedClassState(myPassesData?.getMyPurchasedPasses?.data);
  }, [myPassesData]);

  const hasActivePass = useMemo(() => {
    const purchases = myPassesData?.getMyPurchasedPasses?.data;
    if (!Array.isArray(purchases)) return false;
    return purchases.some((purchase) => purchase?.status === "ACTIVE");
  }, [myPassesData]);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);
  const schedulePassesRedirect = (state?: Record<string, any>) => {
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }
    redirectTimeoutRef.current = setTimeout(() => {
      navigate("/passes", state ? { state } : undefined);
    }, 2200);
  };

  const handleBookAction = async (classItem: any) => {
    const classId = classItem?.originalId || classItem?.id;
    const sessionDate = classItem?.singleDay || classItem?.day;
    if (!classId || !sessionDate || !hasActivePass) {
      setSnackbarMessage("Please purchase a pass to book a class.");
      setSnackbarType("error");
      setSnackbarOpen(true);
      schedulePassesRedirect({
        bookingContext: {
          classId,
          sessionDate,
          className: classItem?.className,
        },
      });
      return;
    }

    const { data: response } = await bookClassWithPass({
      variables: { classId, sessionDate },
    });

    if (response?.bookClassWithPass?.success) {
      setSnackbarMessage(response.bookClassWithPass.message || "Class booked successfully.");
      setSnackbarType("success");
      setSnackbarOpen(true);
      setSelectedClassToBook(null);
      await Promise.all([refetch(), refetchMyPasses()]);
      return;
    }

    setSnackbarMessage(
      response?.bookClassWithPass?.message || "Please purchase a pass to book this class.",
    );
    setSnackbarType("error");
    setSnackbarOpen(true);
    schedulePassesRedirect({
      bookingContext: {
        classId,
        sessionDate,
        className: classItem?.className,
      },
    });
  };

  const rawClasses = data?.getClassList?.data || [];

  const classes = useMemo(() => {
    const filteredByDay = rawClasses.flatMap((cls: any) => {
      let dates: string[] = [];
      if (Array.isArray(cls.scheduledDates) && cls.scheduledDates.length > 0) {
        dates = cls.scheduledDates;
      } else if (typeof cls.day === "string" && cls.day) {
        dates = [cls.day];
      } else if (Array.isArray(cls.day) && cls.day.length > 0) {
        dates = cls.day;
      }
      const weekDates = dates.filter(date => date >= startDate && date <= endDate);
      return weekDates.map((date: string) => ({
        ...cls,
        id: `${cls.id}-${date}`,
        originalId: cls.id,
        singleDay: date,
      }));
    });

    return filteredByDay.filter((cls: any) => {
      const matchesActivity = selectedActivity ? cls.classType === selectedActivity : true;
      const matchesInstructor = selectedInstructor ? cls.instructorName === selectedInstructor : true;
      return matchesActivity && matchesInstructor;
    }).sort((a: any, b: any) => {
      const dateCompare = (b.singleDay || "").localeCompare(a.singleDay || "");
      if (dateCompare !== 0) return dateCompare;
      return (a.startTime || "").localeCompare(b.startTime || "");
    });
  }, [rawClasses, selectedActivity, selectedInstructor, startDate, endDate]);

  const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, value: number) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ width: "100%", py: 2 }}>
      <Box
        sx={{
          mb: 4,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          alignItems: "center",
        }}
      >
        <TextField
          placeholder="Search for classes, instructors..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9CA3AF" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            width: { xs: "100%", md: "auto" },
            "& .MuiOutlinedInput-root": {
              borderRadius: "999px",
              backgroundColor: "#F9FAFB",
              height: "48px",
              fontSize: { xs: "12px", md: "14px" },
              "& fieldset": { borderColor: "#E5E7EB" },
              "&:hover fieldset": { borderColor: "#D1D5DB" },
              "&.Mui-focused fieldset": { borderColor: "#92D026" },
            },
          }}
        />

        <Box sx={{ display: "flex", gap: 1.5, width: { xs: "100%", md: "auto" } }}>
          {[
            {
              label: "Activity",
              state: selectedActivity,
              setState: setSelectedActivity,
              options: Object.keys(CLASS_TYPE_UI).map(k => ({ value: k, label: CLASS_TYPE_UI[k].label })),
              anchorEl: activityAnchorEl,
              setAnchorEl: setActivityAnchorEl,
            },
            {
              label: "Instructor",
              state: selectedInstructor,
              setState: setSelectedInstructor,
              options: Array.from(new Set(rawClasses.map((c: any) => c.instructorName))).map(t => ({ value: t as string, label: t as string })),
              anchorEl: instructorAnchorEl,
              setAnchorEl: setInstructorAnchorEl,
            },
          ].map((filter) => (
            <React.Fragment key={filter.label}>
              <Button
                variant="outlined"
                endIcon={<ExpandMoreIcon />}
                onClick={(e) => filter.setAnchorEl(e.currentTarget)}
                sx={{
                  borderRadius: "32px",
                  textTransform: "none",
                  color: filter.state ? "#92D026" : "#4A5568",
                  borderColor: filter.state ? "#92D026" : "#E2E8F0",
                  px: { xs: 2, md: 3 },
                  py: 1.2,
                  minWidth: { xs: "120px", md: "170px" },
                  fontSize: { xs: "13px", md: "15px" },
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  backgroundColor: filter.state ? "rgba(146, 208, 38, 0.06)" : "#FFFFFF",
                  boxShadow: "0px 1px 0px rgba(17, 24, 39, 0.02)",
                  "&:hover": {
                    borderColor: "#92D026",
                    backgroundColor: "rgba(146, 208, 38, 0.08)",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "clip",
                  }}
                >
                  {filter.state ? filter.options.find(o => o.value === filter.state)?.label : filter.label}
                </Typography>
              </Button>
              <Menu
                anchorEl={filter.anchorEl}
                open={Boolean(filter.anchorEl)}
                onClose={() => filter.setAnchorEl(null)}
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: "20px",
                      mt: 1,
                      boxShadow: "0px 14px 30px rgba(15, 23, 42, 0.08)",
                      border: "1px solid #E2E8F0",
                      minWidth: "240px",
                      overflow: "hidden",
                    },
                  },
                }}
              >
                <MenuItem
                  onClick={() => { filter.setState(null); filter.setAnchorEl(null); setPage(0); }}
                  sx={{
                    fontSize: "14px",
                    color: "#718096",
                    py: 1.5,
                    px: 2.25,
                  }}
                >
                  All {filter.label}s
                </MenuItem>
                {filter.options.map((opt) => (
                  <MenuItem
                    key={opt.value}
                    onClick={() => { filter.setState(opt.value); filter.setAnchorEl(null); setPage(0); }}
                    sx={{
                      fontSize: "14px",
                      fontWeight: filter.state === opt.value ? 700 : 500,
                      color: filter.state === opt.value ? "#92D026" : "inherit",
                      py: 1.5,
                      px: 2.25,
                      "&:hover": {
                        backgroundColor: "rgba(146, 208, 38, 0.06)",
                      },
                    }}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Menu>
            </React.Fragment>
          ))}
        </Box>
      </Box>

      {loading ? (
        <CenteredLoader />
      ) : classes.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography
            variant="h6"
            color="textSecondary"
            sx={{ fontSize: { xs: "13px", sm: "1rem" } }}
          >
            {selectedDayForFilter ? "No classes found for this day." : "No classes found for this week."}
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {classes.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((cls: any) => {
              const classTypeConfig = CLASS_TYPE_UI[cls.classType] || {
                label: cls.classType,
                bg: "#F1F8EE",
                color: "#6CBC44",
              };
              const classDate = new Date((cls.singleDay || cls.day) + "T00:00:00");
              const weekday = classDate.toLocaleDateString("en-US", { weekday: "short" });
              const month = classDate.toLocaleDateString("en-US", { month: "short" });
              const day = classDate.getDate();
              const formattedDate = `${weekday} ${day} ${month}`;
              const timeRange = `${formatTo12Hour(cls.startTime)} - ${formatTo12Hour(cls.endTime)}`;
              const dateTimeDisplay = `${formattedDate} ${timeRange}`;
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const isPast = classDate < today;

              const isJoined =
                !isPast &&
                joinedClassState.sessionKeys.has(`${cls.originalId || cls.id}|${cls.singleDay || cls.day || ""}`);

              return (
                <Grid size={{ xs: 12, sm: 12, md: 4 }} key={cls.id}>
                  <Card
                    sx={{
                      borderRadius: "16px",
                      boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.08)",
                      border: "1px solid #F1F5F9",
                      position: "relative",
                      overflow: "hidden",
                      backgroundColor: "#fff",
                      transition: "transform 0.3s ease, boxShadow 0.3s ease",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0px 15px 40px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                    onClick={() => {
                      const now = new Date();
                      const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();
                      const parseTime = (t: string) => {
                        const [h, m] = t.split(":").map(Number);
                        return h * 60 + m;
                      };
                      const startTimeMinutes = parseTime(cls.startTime);
                      const isToday = classDate.getTime() === today.getTime();
                      const isStarted = isToday && currentTimeMinutes >= startTimeMinutes;

                      if (isJoined || (!isPast && !isStarted)) {
                        if (isJoined || hasActivePass) {
                          setSelectedClassToBook(cls);
                        } else {
                          navigate("/passes", {
                            state: {
                              bookingContext: {
                                classId: cls.originalId || cls.id,
                                sessionDate: cls.singleDay || cls.day,
                                className: cls.className,
                              },
                            },
                          });
                        }
                      }
                    }}
                  >
                    <Box sx={{ position: "relative", height: "240px", overflow: "hidden" }}>
                      {cls.coverImage ? (
                        <Box
                          component="img"
                          src={
                            cls.coverImage.startsWith("data:")
                              ? cls.coverImage
                              : cls.coverImage.startsWith("http")
                              ? cls.coverImage
                              : `data:image/*;base64,${cls.coverImage}`
                          }
                          alt={cls.className}
                          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            backgroundColor: "#f5f5f5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        />
                      )}
                      
                      <Box
                        sx={{
                          position: "absolute",
                          top: 16,
                          left: 16,
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          backdropFilter: "blur(4px)",
                          color: "#92D026",
                          px: 2,
                          py: 0.8,
                          borderRadius: "999px",
                          fontWeight: 800,
                          fontSize: "10px",
                          textTransform: "uppercase",
                          letterSpacing: "1px",
                          boxShadow: "0px 2px 4px rgba(0,0,0,0.05)",
                        }}
                      >
                        {classTypeConfig.label}
                      </Box>

                      {isJoined && (
                        <Box sx={{ position: "absolute", top: 12, right: 12, zIndex: 10 }}>
                          <Chip
                            icon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important", color: "#2E7D32 !important" }} />}
                            label="Joined"
                            size="small"
                            sx={{
                              backgroundColor: "#E8F5E9",
                              color: "#2E7D32",
                              fontWeight: 700,
                              fontSize: "11px",
                              height: "24px",
                            }}
                          />
                        </Box>
                      )}
                    </Box>

                    <CardContent sx={{ p: 3 }}>
                      <TruncatedTitle
                        text={cls.className}
                        variant="h6"
                        sx={{
                          color: "#1E293B",
                          fontSize: "1.25rem",
                          mb: 2,
                          lineHeight: 1.2,
                          fontWeight: 800,
                        }}
                      />

                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
                        <PersonOutlineIcon sx={{ color: "#92D026", fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 600, fontSize: "14px" }}>
                          Instructor: {cls.instructorName}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <AccessTimeIcon sx={{ color: "#92D026", fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 600, fontSize: "14px" }}>
                          {dateTimeDisplay}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", gap: 1.5 }}>
                      {(() => {
                        const now = new Date();
                        const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();
                        const parseTime = (t: string) => {
                          const [h, m] = t.split(":").map(Number);
                          return h * 60 + m;
                        };
                        const startTimeMinutes = parseTime(cls.startTime);
                        const endTimeMinutes = parseTime(cls.endTime);
                        const isToday = classDate.getTime() === today.getTime();
                        const isStarted = isToday && currentTimeMinutes >= startTimeMinutes;
                        const isEnded = isToday && currentTimeMinutes >= endTimeMinutes;

                        const isDisabled = (isPast || isStarted || isEnded) && !isJoined;
                        const buttonText = isJoined ? "Joined" : isPast || isEnded ? "Closed" : isStarted ? "Ongoing" : "Book Now";
                        const buttonColor = isDisabled ? "#FFEBEE" : isJoined ? "#E8F5E9" : "#5AA839";
                        const textColor = isDisabled ? "#D32F2F" : isJoined ? "#2E7D32" : "#fff";

                        return (
                          <Button
                            fullWidth
                            variant="contained"
                            disabled={isDisabled}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isJoined || (!isPast && !isStarted && !isEnded)) {
                                if (isJoined || hasActivePass) {
                                  setSelectedClassToBook(cls);
                                } else {
                                  navigate("/passes", {
                                    state: {
                                      bookingContext: {
                                        classId: cls.originalId || cls.id,
                                        sessionDate: cls.singleDay || cls.day,
                                        className: cls.className,
                                      },
                                    },
                                  });
                                }
                              }
                            }}
                            sx={{
                              backgroundColor: buttonColor,
                              color: textColor,
                              borderRadius: "10px",
                              textTransform: "none",
                              fontSize: "14px",
                              py: 1,
                              fontWeight: 600,
                              boxShadow: "none",
                              cursor: isDisabled ? "default" : "pointer",
                              pointerEvents: "auto",
                              "&:hover": {
                                backgroundColor: isDisabled ? buttonColor : isJoined ? "#E8F5E9" : "#4A8F2A",
                                boxShadow: isDisabled || isJoined
                                  ? "none"
                                  : "0px 4px 12px rgba(90, 168, 57, 0.2)",
                              },
                              "&.Mui-disabled": {
                                backgroundColor: buttonColor,
                                color: textColor,
                              },
                            }}
                          >
                            {buttonText}
                          </Button>
                        );
                      })()}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          </Grid>

      {!loading && classes.length > (isMobile ? 3 : 9) && (
        <Box sx={{ mt: 4, mb: 2 }}>
          {isMobile ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                py: 2,
                px: 2,
                backgroundColor: "#ffffff",
                borderTop: "1px solid #F3F4F6",
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontSize: "14px", color: "#1F2937", fontWeight: 500, mr: 2 }}
              >
                {page * rowsPerPage + 1} –{" "}
                {Math.min((page + 1) * rowsPerPage, classes.length)}
                    <span style={{ marginLeft: "8px", marginRight: "8px", color: "#9CA3AF" }}>of</span>
                    {classes.length}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                      sx={{
                        width: "32px", height: "32px",
                        border: "1px solid #E5E7EB", borderRadius: "8px", color: "#1F2937",
                        "&:disabled": { color: "#D1D5DB", borderColor: "#F3F4F6" },
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={(page + 1) * rowsPerPage >= classes.length}
                      sx={{
                        width: "32px", height: "32px",
                        border: "1px solid #E5E7EB", borderRadius: "8px", color: "#1F2937",
                        "&:disabled": { color: "#D1D5DB", borderColor: "#F3F4F6" },
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <TablePagination
                  component="div"
                  count={classes.length}
                  page={page}
                  onPageChange={handlePageChange}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={[9, 18, 27]}
                  ActionsComponent={CustomPaginationActions}
                  sx={{
                    borderTop: "1px solid #E5E7EB",
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                      fontSize: "13px", color: "#6B7280",
                    },
                    "& .MuiTablePagination-displayedRows": { marginRight: "16px" },
                    "& .MuiTablePagination-actions": {
                      display: "flex", alignItems: "center", gap: "12px", ml: 2,
                    },
                  }}
                />
              )}
            </Box>
          )}
        </>
      )}

      <ClassPurchaseDrawer
        open={!!selectedClassToBook}
        onClose={() => setSelectedClassToBook(null)}
        classData={selectedClassToBook}
        requiresPass={!hasActivePass}
        isJoined={
          selectedClassToBook
            ? new Date(`${selectedClassToBook.singleDay || selectedClassToBook.day || ""}T00:00:00`) >= new Date(new Date().setHours(0, 0, 0, 0)) &&
              joinedClassState.sessionKeys.has(
                `${selectedClassToBook.originalId || selectedClassToBook.id}|${selectedClassToBook.singleDay || selectedClassToBook.day || ""}`,
              )
            : false
        }
        onBookAction={() => {
          if (selectedClassToBook) {
            void handleBookAction(selectedClassToBook);
          }
        }}
      />
      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default UserCalenderClassesCardView;
